import type { Duration as DateFnsDuration } from 'date-fns';

import { Timestamp } from '@viamrobotics/web-client/google/protobuf/timestamp';

/** Convert an API timestamp into a date object. */
export const timestampToDate = (timestamp: Timestamp): Date => {
  return Timestamp.toDate(timestamp);
};

/** Convert an API timestamp into epoch milliseconds. */
export const timestampToEpoch = (timestamp: Timestamp): number => {
  return timestampToDate(timestamp).getTime();
};

export const compareTimestamps = (
  timestamp1: Timestamp | undefined,
  timestamp2: Timestamp | undefined
) => {
  if (!timestamp1 && !timestamp2) {
    return 0;
  }
  if (!timestamp1) {
    return -1;
  }
  if (!timestamp2) {
    return 1;
  }
  return timestampToEpoch(timestamp1) - timestampToEpoch(timestamp2);
};

const localDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: '2-digit',
  year: '2-digit',
  hour: 'numeric',
  minute: 'numeric',
});

const localDateFormatterWithSeconds = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: '2-digit',
  year: '2-digit',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
});

export const timestampToLocale = (
  timeStamp: Timestamp | undefined,
  showSeconds?: boolean
): string => {
  if (timeStamp === undefined) {
    return '--';
  }
  const dateObj = timestampToDate(timeStamp);
  if (showSeconds) {
    return localDateFormatterWithSeconds.format(dateObj);
  }
  return localDateFormatter.format(dateObj);
};

export const timestampToLocaleDate = (
  timeStamp: Timestamp | undefined
): string => {
  if (timeStamp === undefined) {
    return '--';
  }
  const dateObj = timestampToDate(timeStamp);
  return dateObj.toLocaleDateString();
};

export const timestampToISODate = (
  timeStamp: Timestamp | undefined
): string => {
  if (timeStamp === undefined) {
    return '--';
  }
  const dateObj = timestampToDate(timeStamp);
  return dateObj.toISOString();
};

interface Duration extends DateFnsDuration {
  milliseconds?: number;
}

export const getFormattedDuration = (dur: Duration): string => {
  const formatedStrings = [];
  if (dur.days && dur.days > 0) {
    formatedStrings.push(`${dur.days}d`);
  }
  if (dur.hours && dur.hours > 0) {
    formatedStrings.push(`${dur.hours}h`);
  }
  if (dur.minutes && dur.minutes > 0) {
    formatedStrings.push(`${dur.minutes}m`);
  }
  if (dur.seconds && dur.seconds > 0) {
    formatedStrings.push(`${dur.seconds}s`);
  }
  if (dur.milliseconds && dur.milliseconds > 0) {
    formatedStrings.push(`${dur.milliseconds}ms`);
  }

  return formatedStrings.join(' ');
};

// Recursively converts all Protobuf Timestamp objects to JavaScript Date objects
// within arrays and objects.
export const convertTimestampsToDates = (value: unknown): unknown => {
  if (value && typeof value === 'object') {
    // Check that the object only contains seconds and nanos properties.
    // If so, we assume that it's a Protobuf Timestamp.
    const keys = Object.keys(value);
    if (
      keys.length === 2 &&
      keys.includes('seconds') &&
      keys.includes('nanos')
    ) {
      return timestampToDate(value as Timestamp);
    }

    if (Array.isArray(value)) {
      return value.map(convertTimestampsToDates);
    }

    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key,
        convertTimestampsToDates(val),
      ])
    );
  }

  return value;
};

export enum TimestampFormat {
  ISO = 'ISO',
  LOCAL = 'Local',
}

export const TIMESTAMP_FORMAT_OPTIONS = [
  TimestampFormat.ISO,
  TimestampFormat.LOCAL,
];
